/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, Heading, IModuleProps, INodeProps, Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { AttributeValue, IContext } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { dXC_GetFreightChargesForPDPAsync } from '../../data-actions/generated/DataActionExtension.g';
import { IDXC_GetFreightChargesServiceResponse, IDXC_ItemLine } from '../../data-actions/generated/DataServiceEntities.g';
import geoLocation, { BingMapsLocationsInput } from '../../data-actions/geo-location/bing-maps-locations';
import { IGetFreightChargesServiceResponse } from '../../models/GetFreightChargesServiceResponse';
import { ICittaFreightChargesData } from './citta-freight-charges.data';
import { ICittaFreightChargesProps } from './citta-freight-charges.props.autogenerated';

interface IFreightChargeState {
    errorMessage: string;
    isOpen: boolean;
    postalCode?: string;
    latitude: string;
    longitude: string;
    freightResponse?: IGetFreightChargesServiceResponse;
    isError: boolean;
}

export interface ICittaFreightChargesViewProps extends ICittaFreightChargesProps<ICittaFreightChargesData> {
    ProductFreightContainer: IModuleProps;
    ProductFreightAttribute: INodeProps;
    ProductNoFreightAttribute: INodeProps;
    ProductFreightAttributeModal: INodeProps;
    productFreightAmount: React.ReactNode;
    productFreightMessage: React.ReactNode;
    productFreightCalculate: React.ReactNode;
    productFreightUpdate: React.ReactNode;
    productFreightModel: React.ReactNode;
    productNoFreight: React.ReactNode;
    freightAttribute: boolean;
    freightResponseCheck: boolean;
}

/**
 *
 * CittaFreightCharges component
 * @extends {React.PureComponent<ICittaFreightChargesProps<ICittaFreightChargesData>>}
 */
@observer
class CittaFreightCharges extends React.PureComponent<ICittaFreightChargesProps<ICittaFreightChargesData>, IFreightChargeState> {

    public state: IFreightChargeState = {
        errorMessage: '',
        isOpen: false,
        postalCode: '',
        latitude: '',
        longitude: '',
        freightResponse: undefined,
        isError: false
    };

    private _productFreightAttribute: string = this.props.context.app.config.freightValue && this.props.context.app.config.freightValue.toUpperCase() || 'FREIGHT ITEM';
    private _PostalCodeInputRef: React.RefObject<HTMLInputElement>;
    private _showAvailablityText: boolean = false;

    constructor(props: ICittaFreightChargesProps<ICittaFreightChargesData>) {
        super(props);
        this._PostalCodeInputRef = React.createRef();
        this._closeModal = this._closeModal.bind(this);
        this._openModal = this._openModal.bind(this);
        this._handleSearch = this._handleSearch.bind(this);
        this._handleChange = this._handleChange.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        reaction(
            () => this.props.config.onUpdateQuantityFreightCheck && this.props.data.productQuantityState.result && this.props.data.productQuantityState.result.productQuantity && this.props.data.productQuantityState.result.productQuantity,
            () => {
                if (this.props.config.onUpdateQuantityFreightCheck && this.props.data.productQuantityState.result && this.props.data.productQuantityState.result.productQuantity && this.props.data.productQuantityState.result.productQuantity) {
                    this._getNewFreightCharges(this.state.postalCode).catch(e => {
                        this.props.telemetry.error(`Something went wrong ------${e}`);
                    });
                }
            }
        );

        // tslint:disable-next-line: no-this-assignment
        const that = this;
        const freightAttribute: AttributeValue[] | undefined = this.props.data.productAttributes.result;
        if (freightAttribute && this._checkFreightAttributes()) {
            if (navigator && navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async position => {
                        this.setState({ latitude: position.coords.latitude.toString(), longitude: position.coords.longitude.toString() });
                        const response: IDXC_GetFreightChargesServiceResponse = await this._getItemFreightCharges();
                        this.setState({ isOpen: false, freightResponse: response, errorMessage: '' });
                    },
                    error => {
                        if (error.code === error.PERMISSION_DENIED) {
                            that.setState({ isOpen: false, errorMessage: '' });
                        }
                    });
            }
        } else {
            this.setState({ isOpen: false, freightResponse: undefined, errorMessage: '' });
        }
    }

    public render(): JSX.Element | null {
        const config = this.props.config;
        const responseObj: IGetFreightChargesServiceResponse | undefined = this.state.freightResponse;
        const freightAttributeCheck = this._checkFreightAttributes();
        let freightResponseCheck: boolean = false;
        if (responseObj && this.state.postalCode && !this.state.isError) {
            freightResponseCheck = true;
        }
        const CittaFreightChargesViewProps = {
            ...this.props,
            ProductFreightContainer: {
                moduleProps: this.props,
                className: classnames('product-freight-charges', config.className)
            },
            ProductFreightAttribute: {
                className: 'product-freight'
            },
            ProductFreightAttributeModal: {
                className: ''
            },
            ProductNoFreightAttribute: {
                className: ''
            },
            productFreightAmount: this._renderProductFreightAmount(),
            productFreightMessage: this._renderProductFreightMessage(),
            productFreightCalculate: this._renderProductFreightCalculate(),
            productFreightUpdate: this._renderProductFreightUpdate(),
            productFreightModel: this._renderProductFreightModal(),
            productNoFreight: this._renderProductNoFreight(),
            freightAttribute: freightAttributeCheck,
            freightResponseCheck: freightResponseCheck
        };
        return this.props.renderView(CittaFreightChargesViewProps) as React.ReactElement;
    }

    private _checkFreightAttributes = (): boolean => {
        const prodAttributes: AttributeValue[] | undefined = this.props && this.props.data && this.props.data.productAttributes && this.props.data.productAttributes.result;
        if (prodAttributes && prodAttributes.length) {
            const filterAttributes = prodAttributes.filter(
                attribute => attribute.Name && attribute.Name.toUpperCase() === this._productFreightAttribute
            );
            if (filterAttributes && filterAttributes.length) {
                if (!filterAttributes[0].BooleanValue) {
                    this._showAvailablityText = true;
                }
                return filterAttributes[0].BooleanValue || false;
            } else {
                this._showAvailablityText = false;
                return false;
            }
        }
        return false;
    }

    private _handleChange = (ref: React.RefObject<HTMLInputElement>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ errorMessage: '' });
    }

    private _handleSearch = async (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        const postalCodeInput = this._PostalCodeInputRef && this._PostalCodeInputRef.current ? this._PostalCodeInputRef.current.value : '';
        if (postalCodeInput.length > 4) {
            this.setState({ errorMessage: this.props.config.postalCodeInputErrorMessage1! });
        } else if (!this._isValidPostalCode(postalCodeInput)) {
            this.setState({ errorMessage: this.props.config.postalCodeInputErrorMessage2! });
        } else {
            const response: IDXC_GetFreightChargesServiceResponse = await this._getItemFreightCharges(postalCodeInput);
            if (response.Result === 'false') {
                this.setState({ errorMessage: response.Message!, isError: true });
            } else if (response && response.Result) {
                this.setState({ errorMessage: '', freightResponse: response, postalCode: postalCodeInput, isError: false });
            } else if (response && !response.Result) {
                this.setState({ errorMessage: response.Message!, isError: true });
            }
        }
    }

    private _getNewFreightCharges = async (postalCode?: string): Promise<void> => {
        const response: IDXC_GetFreightChargesServiceResponse = await this._getItemFreightCharges(postalCode);
        if (response.Result === 'false') {
            this.setState({ errorMessage: response.Message!, isError: true });
        } else if (response && response.Result) {
            this.setState({ errorMessage: '', freightResponse: response, isError: false });
        } else if (response && !response.Result) {
            this.setState({ errorMessage: response.Message!, isError: true });
        }
    }

    private _getItemFreightCharges(postalCode?: string): Promise<IGetFreightChargesServiceResponse> {
        const itemId = this.props && this.props.data && this.props.data.product && this.props.data.product.result ? this.props.data.product.result.ItemId : '';
        const response: IGetFreightChargesServiceResponse = { ChargesAmount: 0 };
        const itemLine: IDXC_ItemLine = { ItemId: itemId, Quantity: this.props.data.currentQuantity.result };
        const ctx: IContext = { callerContext: this.props.context.actionContext };

        if (!postalCode) {
            const bingAppKey = this.props && this.props.config && this.props.config.bingApiAppKey ? this.props.config.bingApiAppKey : '';
            const bingApiEndpoint = this.props && this.props.config && this.props.config.bingApiEndPoint || '';
            const searchInput: BingMapsLocationsInput = new BingMapsLocationsInput(this.state.latitude, this.state.longitude, bingAppKey, bingApiEndpoint);
            geoLocation(searchInput, this.props.context.actionContext).then(
                (result) => {
                    if (result) {
                        postalCode = result.postalCode;
                        this.setState({ postalCode: postalCode });
                        dXC_GetFreightChargesForPDPAsync(ctx, postalCode!, itemLine).then(
                            (responseObj) => {
                                if (responseObj) {
                                    if (responseObj.Result === 'false') {
                                        this.setState({ errorMessage: responseObj.Message!, isError: true });
                                    } else {
                                        this.setState({ errorMessage: '', freightResponse: responseObj, isError: false });
                                    }
                                } else {
                                    this.setState({ isError: true });
                                }
                            }
                        );
                    } else {
                        postalCode = '';
                        return;
                    }
                });
        } else {
            this.setState({ postalCode: postalCode });
            return dXC_GetFreightChargesForPDPAsync(ctx, postalCode, itemLine);
        }
        return Promise.resolve(response);
    }

    private _closeModal(): void {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen
        }));
    }
    private _openModal(): void {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen
        }));
    }

    private _isValidPostalCode = (postalCode: string): boolean => {
        const regex = /^[0-9]+[0-9]*$/;
        return regex.test(postalCode);
    }

    private _getFormattedPrice(freightAmount: number): React.ReactNode {
        const formattedPrice = this.props.context.cultureFormatter.formatCurrency(freightAmount);
        return (
            <span>{formattedPrice}</span>
        );
    }

    private _renderProductFreightAmount(): React.ReactNode {
        const responseObj: IGetFreightChargesServiceResponse | undefined = this.state.freightResponse;
        if (responseObj) {
            return (
                <div className='product-freight-amount'>
                    {this._getFormattedPrice(responseObj.ChargesAmount)} {this.props.resources.freight_estimatedShippingText}
                </div>
            );
        } else {
            return null;
        }
    }

    private _renderProductFreightMessage(): React.ReactNode {
        const responseObj: IGetFreightChargesServiceResponse | undefined = this.state.freightResponse;
        const resources = this.props.resources;
        const postalCode = this.state.postalCode || '';
        const freightBasedOnCityName = responseObj && postalCode ? `${resources.freight_basedOnText}: ${postalCode}` : '';
        return (
            <div className='product-freight-message'>{freightBasedOnCityName}</div>
        );
    }

    private _renderProductFreightUpdate(): React.ReactNode {
        const resources = this.props.resources;
        return (
            <div className='product-freight-update'>
                <Button
                    className='product-freight-update-link'
                    id={'noglyphid'}
                    aria-label={resources.freight_updateLocationLinkText}
                    title={resources.freight_updateLocationLinkText}
                    href='#'
                    onClick={this._openModal}
                    outline={true}
                >
                    {resources.freight_updateLocationLinkText}
                </Button>
            </div>
        );
    }

    private _renderProductFreightCalculate(): React.ReactNode {
        const resources = this.props.resources;
        return (
            <div className='product-freight-update'>
                <Button
                    className='product-freight-update-link'
                    id={'noglyphid'}
                    aria-label={resources.freight_updateLocationLinkAltText}
                    title={resources.freight_updateLocationLinkAltText}
                    href='#'
                    onClick={this._openModal}
                    outline={true}
                >
                    {resources.freight_updateLocationLinkAltText}
                </Button>
            </div>
        );
    }
    private _renderProductNoFreight(): React.ReactNode | null {
        const { config, resources } = this.props;
        const showAvailablityTextConfig = config.freightCheck ? config.freightCheck : false;
        // Show availability based in freight attribute as configured in appsetting and module configuration
        if (showAvailablityTextConfig && this._showAvailablityText) {
            const availableForLabelText = resources.freight_availableForLabel ? resources.freight_availableForLabel : 'Available for';
            const availableForValue = config.availableFor ? config.availableFor : 'Click & Collect';
            return (
                <div className='product-no-freight'>
                    <span className='product-no-freight-label' aria-label={availableForLabelText}>{availableForLabelText}</span>
                    <span className='product-no-freight-value' aria-label={availableForValue}>{availableForValue}</span>
                </div>
            );
        } else {
            return null;
        }
    }

    private _renderProductFreightModal(): React.ReactNode {
        const responseObj: IGetFreightChargesServiceResponse | undefined = this.state.freightResponse;
        const { config, resources } = this.props;
        return (
            <Modal
                autoFocus={config.autoFocus}
                fade={config.fade}
                isOpen={this.state.isOpen}
                horizontalPosition={config.horizontalPosition}
                verticalPosition={config.verticalPosition}
                zIndex={config.zIndex}
                toggle={this._closeModal}
                applicationNode={'rsg-root'}
                className={classnames('modal-dialog freight-modal align-items-center ml-auto mr-auto', config.lightboxClassName)}
            >
                <ModalHeader toggle={this._closeModal}>{config.modalHeader}</ModalHeader>
                <ModalBody>
                    <form className='freight-form' name='freight-form' role='Search' autoComplete='off' onSubmit={this._handleSearch}>
                        <div className='freight-form-content'>
                            <Heading text={config.formHeadingText || ''} className='freight-form-content-heading' tag='h1' />
                            <p className='freight-form-content-inputlabel'>{config.postalCodeInputLabel}</p>
                            <div className='freight-form-content-postcodeWrap'>
                                <input
                                    ref={this._PostalCodeInputRef}
                                    type='text'
                                    onChange={this._handleChange(this._PostalCodeInputRef)}
                                    className='freight-form-content-postcodeWrap-code'
                                    aria-label={resources.freight_updateLocationLinkText}
                                />
                                <div className='freight-form-content-postcodeWrap-search'>
                                    <Button
                                        color='nocolor'
                                        aria-label='Search'
                                        onClick={this._handleSearch}
                                    />
                                </div>
                            </div>
                            {this.state.errorMessage ? <div className='freight-form-content-error'>
                                {this.state && this.state.errorMessage ?
                                    <span className='freight-form-content-error-message'>
                                        {this.state.errorMessage}
                                    </span> : null
                                }
                            </div> : null}
                            {responseObj ?
                                <div>
                                    <div className='freight-form-content-grid'>
                                        <div className='freight-form-content-grid-postalcode'>
                                            <p>{resources.freight_postCode}</p>
                                            <h1 id='postCode'>{this.state.postalCode ? this.state.postalCode : ''}</h1>
                                        </div>
                                        <div className='freight-form-content-grid-delivery'>
                                            <p className='freight-form-content-grid-delivery-header'>{resources.freight_delivery}</p>
                                            <h2 id='postCodeCost'>{responseObj ? this._getFormattedPrice(responseObj.ChargesAmount) : null}</h2>
                                        </div>
                                    </div>
                                    <div className='freight-form-content-deliveryInfoBottom'>
                                        <p>{config.deliveryInfoBottom1}</p>
                                        <p>{config.deliveryInfoBottom2}</p>
                                        <p>{config.deliveryInfoBottom3}</p>
                                    </div>
                                </div> : null}
                        </div>
                    </form>
                </ModalBody>
            </Modal>
        );
    }

}

export default CittaFreightCharges;
